import type { SfxId } from '../data/audio-manifest';
import type { CoreEvent } from '../core/tick';

/**
 * Which sound each event of the core triggers (GDD §14.3, issues #76 and #100). The core knows no audio: it only
 * announces what happened. LIFE_LOST is only emitted for real damage (#37), CHECKPOINT_ACTIVATED once per
 * checkpoint (#38) and LEVEL_COMPLETED ends the airstrip level with the wheels on the ground (#72 will
 * refine it with its own event); a boss level ends with the stamp of El Salto instead (see `sfxFor`).
 * Effects v2: INTERCEPT, the decoy ("Es harina", not verbal), the jump, the parachute, the stamp, the silver
 * coin of El Patrón, and the sounds of the law helicopter and the hippo.
 */
export const SFX_FOR_EVENT: Readonly<Record<string, SfxId>> = {
  FLAP: 'flap',
  DOLLAR_COLLECTED: 'dollar',
  LIFE_LOST: 'impact',
  CHECKPOINT_ACTIVATED: 'checkpoint',
  LEVEL_COMPLETED: 'landing',
  'intercept:warning': 'intercept',
  'decoy:hit': 'decoy-hit',
  'jump:jumped': 'jump',
  'jump:parachute': 'parachute',
  'jump:delivered': 'stamp',
  'boss:coin': 'silver-coin',
  'en02:amber': 'en02-amber',
  'en02:burst': 'en02-burst',
  'en11:bubbles': 'en11-bubbles',
  'en11:splash': 'en11-splash',
};

/** The effect of an event, or null: the lead has its own dull thud and a boss level has no airstrip landing. */
export function sfxFor(event: CoreEvent): SfxId | null {
  if (event.type === 'DAMAGING_COLLISION') return event.bossObject === 'lead' ? 'lead-hit' : null;
  if (event.type === 'LEVEL_COMPLETED' && event.jumpGrade !== undefined) return null;
  return SFX_FOR_EVENT[event.type] ?? null;
}

/** A core listener that plays the sound of each event it knows. */
export function sfxListener(player: { play(id: SfxId, tick?: number): void }): (event: CoreEvent) => void {
  return (event) => {
    const id = sfxFor(event);
    if (id) player.play(id, event.tick);
  };
}
